import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileCheck, HandCoins, RefreshCw, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/site";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-13 rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";

const POINTS = [
  { icon: FileCheck, title: "Easy Application", desc: "A quick, straightforward application — no dealership runaround, no pressure." },
  { icon: HandCoins, title: "Financing Available", desc: "We work with a range of lenders to find terms that fit your budget." },
  { icon: RefreshCw, title: "Flexible Options", desc: "All credit situations considered. Trade-ins welcome toward your down payment." },
];

export default function Financing() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle_interest: "", down_payment: "", monthly_income: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Financing | TRX Auto Sales & Service Inc — Used Car Financing Charlotte NC";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/financing", form);
      setDone(true);
      toast.success("Financing application received.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Financing"
        lines={["Your Next Vehicle Is", "Closer Than You Think."]}
        sub="Financing available with an easy application process and flexible options — get behind the wheel sooner."
        image={IMG.amgGt}
      />

      <section className="py-16 md:py-24" data-testid="financing-points">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="h-full bg-trx-black p-10">
                  <p.icon className="h-6 w-6 text-trx-blue" />
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24" data-testid="financing-form-section">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          {done ? (
            <Reveal>
              <div className="border border-trx-blue/40 bg-card p-10 text-center md:p-16" data-testid="financing-confirmation">
                <CheckCircle2 className="mx-auto h-12 w-12 text-trx-blue" />
                <h2 className="mt-6 font-display text-3xl md:text-4xl font-black tracking-tight text-white">Application Received.</h2>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
                  Our financing team will review your application and contact you within one business day.
                  Questions? Call (704) 380-4555.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="border border-white/10 bg-card p-8 md:p-12" data-testid="financing-form">
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">Apply For Financing</h2>
                <p className="mt-2 text-sm text-zinc-500">Takes about two minutes. No obligation.</p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="financing-name" />
                  <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="financing-email" />
                  <Input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="financing-phone" />
                  <Input placeholder="Vehicle of interest" value={form.vehicle_interest} onChange={set("vehicle_interest")} className={inputCls} data-testid="financing-vehicle" />
                  <Input placeholder="Estimated down payment" value={form.down_payment} onChange={set("down_payment")} className={inputCls} data-testid="financing-down-payment" />
                  <Input placeholder="Monthly income" value={form.monthly_income} onChange={set("monthly_income")} className={inputCls} data-testid="financing-income" />
                </div>
                <Textarea
                  placeholder="Anything else we should know…"
                  value={form.message}
                  onChange={set("message")}
                  className="mt-5 min-h-[120px] rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
                  data-testid="financing-message"
                />
                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="financing-submit"
                    className="group flex items-center justify-center gap-3 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50"
                  >
                    {submitting ? "Submitting…" : "Apply For Financing"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <Link
                    to="/contact"
                    data-testid="financing-contact-btn"
                    className="flex items-center justify-center border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-trx-blue hover:text-trx-blue"
                  >
                    Contact Our Team
                  </Link>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
